import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/perm";
import { prefetch, readCache } from "@/lib/cache";
import { LayoutDashboard, Briefcase, LogOut, Settings, Trash2, Menu, X, Shield, Building2, KeyRound, BarChart3, Users } from "lucide-react";
import ChangePasswordDialog from "@/components/app/ChangePasswordDialog";
import GlobalSearch from "@/components/app/GlobalSearch";
import { ROLE_LABELS } from "@/components/app/OrgChart";

export default function Layout({ children, actions }) {
  const { user, tenant, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  // Close the drawer whenever the route changes (user tapped a link inside).
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  // Warm the cache so Jobs / Overview render instantly when opened.
  useEffect(() => {
    ["/jobs", "/analytics/summary"].forEach(p => { if (readCache(p) === undefined) prefetch(p); });
  }, []);
  // Lock body scroll while the drawer is open so the content behind doesn't scroll on iOS.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const items = [
    { href: "/app", label: "Overview", icon: LayoutDashboard },
    { href: "/app/jobs", label: "Jobs", icon: Briefcase },
    { href: "/app/candidates", label: "Candidates", icon: Users },
    { href: "/app/clients", label: "Clients", icon: Building2 },
    ...(can(user, "analytics.view") ? [{ href: "/app/analytics", label: "Analytics", icon: BarChart3 }] : []),
    { href: "/app/recycle", label: "Recycle Bin", icon: Trash2 },
    { href: "/app/settings", label: "Settings", icon: Settings },
    // Platform-owner-only entry — hidden for every other role.
    ...(user?.role === "platform_owner" ? [{ href: "/app/workspaces", label: "Workspaces", icon: Shield }] : []),
  ];

  const Brand = () => (
    <Link to="/app" className="flex items-center gap-2 h-8" data-testid="brand-link">
      {tenant?.logo_data_url ? (
        <img src={tenant.logo_data_url} alt={tenant.name || "Workspace"} className="h-8 max-w-[160px] object-contain" />
      ) : tenant ? (
        <img src="/recruitsift-logo.png" alt="RecruitSift" className="h-8 max-w-[160px] object-contain" />
      ) : (
        <div className="h-8 w-[140px]" aria-hidden="true" />
      )}
    </Link>
  );
  const NavLinks = ({ onClick }) => (
    <nav className="flex-1 space-y-1">
      {items.map((it) => {
        const active = loc.pathname === it.href || (it.href !== "/app" && loc.pathname.startsWith(it.href));
        return (
          <Link
            key={it.href}
            to={it.href}
            onClick={onClick}
            data-testid={`nav-${it.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}`}
          >
            <it.icon className={`w-4 h-4 ${active ? "text-indigo-600" : ""}`} />
            {it.label}
          </Link>
        );
      })}
    </nav>
  );
  const UserFooter = () => (
    <div className="pt-4 border-t border-slate-200 space-y-3">
      <div className="text-xs">
        <div className="text-slate-900 font-medium truncate">{user?.name}</div>
        <div className="text-indigo-600 font-medium truncate" data-testid="user-role-label">{ROLE_LABELS[user?.role] || user?.role}</div>
        <div className="text-slate-500 truncate">{user?.email}</div>
      </div>
      <button
        data-testid="change-password-btn"
        onClick={() => { setMobileOpen(false); setPwOpen(true); }}
        className="w-full flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors"
      >
        <KeyRound className="w-4 h-4" /> Change password
      </button>
      <button
        data-testid="logout-btn"
        onClick={async () => { await logout(); nav("/login"); }}
        className="w-full flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors"
      >
        <LogOut className="w-4 h-4" /> Sign out
      </button>
    </div>
  );

  return (
    <div className="app-light min-h-screen flex" data-testid="app-shell">
      {pwOpen && <ChangePasswordDialog onClose={() => setPwOpen(false)} />}
      {/* Desktop sidebar */}
      <aside className="w-60 border-r border-slate-200 bg-white/70 backdrop-blur hidden md:flex flex-col p-5 sticky top-0 h-screen">
        <div className="mb-10"><Brand /></div>
        <NavLinks />
        <UserFooter />
      </aside>

      {/* Mobile drawer (overlay + sliding panel) */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm md:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-nav-overlay"
            aria-hidden="true"
          />
          <aside
            className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white border-r border-slate-200 flex flex-col p-5 md:hidden shadow-2xl animate-in slide-in-from-left"
            data-testid="mobile-drawer"
          >
            <div className="flex items-center justify-between mb-8">
              <Brand />
              <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" data-testid="mobile-nav-close" aria-label="Close menu">
                <X className="w-5 h-5" />
              </button>
            </div>
            <NavLinks onClick={() => setMobileOpen(false)} />
            <UserFooter />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-md bg-white/75 border-b border-slate-200">
          <div className="px-3 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 shrink-0">
              <button
                className="md:hidden p-2 -ml-1 rounded-lg hover:bg-slate-100 text-slate-700"
                onClick={() => setMobileOpen(true)}
                data-testid="mobile-nav-open"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="md:hidden"><Brand /></div>
              <div className="hidden sm:block tp-chip tp-chip-indigo truncate" data-testid="workspace-chip">{tenant?.name || user?.company || "Workspace"}</div>
            </div>
            <GlobalSearch />
            {actions && <div className="order-last md:order-none w-full md:w-auto flex items-center gap-2 overflow-x-auto md:overflow-visible md:flex-wrap md:justify-end [&>*]:shrink-0" data-testid="header-actions">{actions}</div>}
          </div>
        </header>
        <div className="p-3 sm:p-6">{children}</div>
      </main>
    </div>
  );
}
